import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { lighten, useTheme } from '@mui/material/styles';
import clsx from 'clsx';
import { useMemo } from 'react';

const fontSizes = {
  small: {
    valueMin: '1.4rem',
    valueMax: '3.2rem',
    labelMin: '1.0rem',
    labelMax: '1.4rem',
  },
  large: {
    valueMin: '2.4rem',
    valueMax: '5.2rem',
    labelMin: '1.6rem',
    labelMax: '1.8rem',
  },
};

/**
 * CardNumberColored Component
 * ---------------------------
 * Renderiza una tarjeta visual para dashboards mostrando un valor numérico
 * destacado y un texto descriptivo (label). El componente combina MUI, Emotion
 * y Tailwind, y aplica autoajuste tipográfico responsivo mediante CSS `clamp()`
 * para adaptar dinámicamente el tamaño de fuente al espacio disponible,
 * manteniéndose dentro de límites mínimos y máximos definidos.
 *
 * Comportamiento del ajuste tipográfico
 * ------------------------------------
 * Tanto el valor (`value`) como la etiqueta (`label`) utilizan:
 *
 *    font-size: clamp(MIN, FLUID, MAX)
 *
 * donde:
 *  - MIN  = tamaño mínimo permitido según la prop `size`.
 *  - MAX  = tamaño máximo permitido según la prop `size`.
 *  - FLUID = valor adaptable (e.g. 8vw o 4vw) que permite que el contenido
 *            reduzca o aumente según el tamaño del contenedor o viewport.
 *
 * De esta forma:
 *  - El texto se reduce cuando el contenedor es pequeño.
 *  - El texto crece de forma fluida hasta un punto máximo definido.
 *  - Nunca supera el tamaño máximo ni baja del mínimo.
 *  - No requiere medición por JavaScript ni eventos de resize.
 *
 * Integración con MUI
 * -------------------
 * Este componente:
 *  - Usa <Paper> como contenedor raíz.
 *  - Utiliza `sx` para estilos dinámicos basados en tema (e.g. lighten()).
 *  - Es compatible con tema claro/oscuro.
 *
 * Integración con Tailwind
 * ------------------------
 * Las clases Tailwind se aplican vía `clsx` y conviven sin conflicto con MUI.
 *
 * Props
 * -----
 * @component
 *
 * @param {Object} props - Propiedades del componente.
 *
 * @param {string} props.label
 *   Texto descriptivo que aparece debajo del valor. Opcional.
 *
 * @param {string | number} props.value
 *   Valor principal a mostrar. Normalmente un número o string con formato.
 *
 * @param {string} [props.color]
 *   Color base del card. Puede ser un color hexadecimal o una key de la
 *   paleta MUI (primary, secondary, error, etc.). Si `variant="contained"` y
 *   no se especifica color, se deriva del background actual.
 *
 * @param {'outlined' | 'contained' | 'elevation'} [props.variant='elevation']
 *   Variante visual del contenedor MUI Paper.
 *
 * @param {'small' | 'large'} [props.size='small']
 *   Tamaño base de la tipografía. Define topes mínimos y máximos del auto-scaling.
 *
 * @param {string} [props.className]
 *   Clases Tailwind adicionales a aplicar al contenedor.
 *
 * @param {Object} [props.sx]
 *   Propiedades `sx` adicionales que se fusionan con las del Paper.
 *
 * @param {any} [props...rest]
 *   Cualquier otra propiedad válida para Paper (onClick, elevation, etc.).
 *
 *
 * Métricas tipográficas internas según `size`
 * -------------------------------------------
 * size="small"
 *    value: MIN 14px — FLUID 8vw — MAX 32px
 *    label: MIN 10px — FLUID 4vw — MAX 16px
 *
 * size="large"
 *    value: MIN 24px — FLUID 8vw — MAX 72px
 *    label: MIN 16px — FLUID 4vw — MAX 32px
 *
 *
 * Ejemplo de uso
 * --------------
 * ```jsx
 * <CardNumberColored
 *   label="Active Sessions"
 *   value={128}
 *   color="#1976d2"
 *   variant="contained"
 *   size="large"
 * />
 * ```
 *
 * ```jsx
 * <CardNumberColored
 *   value="87%"
 *   label="CPU Usage"
 *   color="error.main"
 *   className="shadow-lg"
 * />
 * ```
 *
 */
export const CardNumberColored = ({
  className,
  label,
  value,
  color,
  variant = 'elevation',
  size = 'small',
  ...props
}) => {
  const theme = useTheme();

  const fs = useMemo(() => fontSizes[size], [size]);

  return (
    <Paper
      variant={variant}
      sx={{
        backgroundColor:
          color === undefined && variant === 'contained'
            ? lighten(theme.palette.background.default, 0.4)
            : `${color}19`,
      }}
      className={clsx(
        'flex flex-col flex-1 items-center justify-center h-full rounded-2xl min-h-[100px]',
        className,
        variant === 'contained' && `border-none`
      )}
      {...props}
    >
      <Typography
        className="font-semibold leading-none tracking-tight"
        sx={{
          color: (t) => (t.palette.mode !== 'light' ? lighten(color, 0.02) : color),
          fontSize: `clamp(${fs.valueMin}, 8vw, ${fs.valueMax})`,
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>

      {label && (
        <Typography
          className="mt-2 font-medium text-center"
          sx={{
            color: (t) => (t.palette.mode !== 'light' ? lighten(color, 0.02) : color),
            fontSize: `clamp(${fs.labelMin}, 4vw, ${fs.labelMax})`,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      )}
    </Paper>
  );
};
